import type { FundingInstrument } from "../interfaces/funding";
import type { IPaymentState } from "../interfaces/payment";
import { paymentType } from "../interfaces/payment";

/**
 * Quote commit slice for the payer's current "paying with" pick.
 * Saved cards keep id, brand, and last4. PAN, CVV, and the one-shot VGS
 * alias from a just-finished alta stay in memory.
 */
export function fundingSelectionForPersist(state: {
  paymentState: IPaymentState;
  pendingFundingMethod: string | null;
  fundingInstrument: FundingInstrument | null;
}): {
  paymentState: IPaymentState;
  pendingFundingMethod: string | null;
  fundingInstrument: FundingInstrument | null;
} {
  const instrument = state.fundingInstrument;
  const saved =
    instrument?.source === "saved"
      ? {
          source: "saved" as const,
          paymentMethodId: instrument.paymentMethodId,
          brand: instrument.brand,
          last4: instrument.last4,
          ...(instrument.fundingMethod
            ? { fundingMethod: instrument.fundingMethod }
            : {}),
        }
      : null;

  return {
    paymentState: state.paymentState,
    pendingFundingMethod: state.pendingFundingMethod,
    fundingInstrument: saved,
  };
}

/**
 * The list is safe to reconcile against a selected card only after the
 * refresh for this link and this `cardList` settles. A warm cache is a
 * different snapshot and must not count: it can predate the register that
 * just finished.
 */
export function paymentMethodListSettled(input: {
  linkId: string | null | undefined;
  cardList: unknown;
  settled: { linkId: string; cardList: unknown } | null;
}): boolean {
  if (!input.linkId || !input.settled) return false;
  return (
    input.settled.linkId === input.linkId &&
    input.settled.cardList === input.cardList
  );
}

/**
 * A restored card pick whose id is not in the loaded list. Cash and bank
 * rows are left alone. Call only after `paymentMethodListSettled`.
 */
export function persistedCardIsMissing(
  payment: { type: string; cardId?: string | null } | null,
  instrument: FundingInstrument | null,
  registeredCards: readonly { paymentMethodId: string }[]
): boolean {
  const cardId =
    payment?.type === paymentType.CARD ? payment.cardId ?? null : null;
  const savedId =
    instrument?.source === "saved" ? instrument.paymentMethodId : null;
  const id = cardId || savedId;
  if (!id) return false;
  return !registeredCards.some((card) => card.paymentMethodId === id);
}
